"use client"

import Iconfont from "@repo/ui/iconfont"
import { deleteCookie, getCookie } from "cookies-next"
import { AnimatePresence, motion } from "framer-motion"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import React, { CSSProperties, RefObject, useEffect, useMemo, useState } from "react"
import { Button, Col, Row } from "antd"

import { IUserInfo } from "../../../../servers/usercenter/interface"
import { MenuItem } from "../../types"
import { MobileMenuState } from "../../utils/useMobileMenu"

import "../../index.scss"

import { getSiteUrl } from "@repo/shared"

import { useSigninModal } from "../../../../../SignIn"
import { NavigationItem } from "../../../MainNavigation/type"
import { MetalMindLink } from "./_components/MetalMindLink"

const MOBILE_MENU_BASE_TOP = 48
const MOBILE_MEDIA_QUERY = "(max-width: 768px)"

interface MobileMenuProps {
  isOpen: boolean
  menu: MenuItem[]
  userInfo: IUserInfo | null
  mobileMenuState: MobileMenuState
  scrollContainerRef: RefObject<HTMLDivElement | null>
  currentUrl: string
}

/**
 * 移动端菜单组件（客户端组件）
 */
export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  menu,
  userInfo,
  mobileMenuState,
  scrollContainerRef,
  currentUrl,
}) => {
  const {
    expandedMenuKey,
    selectedThirdLevelMenu,
    closeMobileMenu,
    toggleMenuExpansion,
    handleSecondLevelClick,
    handleBackToSecondLevel,
  } = mobileMenuState
  const locale = useLocale()
  const t = useTranslations("HeaderComp")
  const marketsTitle = t("menu.Markets")

  const [announcementOffset, setAnnouncementOffset] = useState(0)

  const memoMenu = useMemo(() => {
    if (getCookie(process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "")) {
      return [
        ...menu,
        {
          title: t("common.MyAccount"),
          children: [
            {
              title: t("common.tabs.account"),
              href: getSiteUrl("user") + `/${locale}` + "/account",
            },
            {
              title: t("common.tabs.my-subscription"),
              href: getSiteUrl("user") + `/${locale}` + "/my-subscription",
            },
            {
              title: t("common.tabs.orderform"),
              href: getSiteUrl("user") + `/${locale}` + "/orderform",
            },
            {
              title: t("common.tabs.my-activities"),
              href: getSiteUrl("user") + `/${locale}` + "/my-activities",
            },
            {
              title: t("common.tabs.my-collection"),
              href: getSiteUrl("user") + `/${locale}` + "/my-collection",
            },
          ],
        },
        {
          title: "Metrix",
          href: getSiteUrl("ai"),
        },
      ]
    } else {
      return [
        ...menu,
        {
          title: "Metrix",
          href: getSiteUrl("ai"),
        },
      ]
    }
  }, [locale, menu, t])

  const loginUrl = useMemo(() => {
    const baseUrl = getSiteUrl("user") + "/" + locale
    const url = encodeURIComponent(currentUrl)
    return `${baseUrl}/login?url=${url}`
  }, [locale, currentUrl])

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      setAnnouncementOffset(0)
      return
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    let bannerEl: HTMLElement | null = null
    let resizeObserver: ResizeObserver | null = null

    const computeOffset = (target?: HTMLElement | null) => {
      const currentBanner = (target ?? bannerEl ?? document.querySelector(".announcementBanner")) as HTMLElement | null
      bannerEl = currentBanner
      const nextOffset =
        mediaQuery.matches && currentBanner ? Math.round(currentBanner.getBoundingClientRect().height) : 0
      setAnnouncementOffset((prev) => (prev === nextOffset ? prev : nextOffset))

      if (resizeObserver && currentBanner) {
        resizeObserver.observe(currentBanner)
      }
    }

    resizeObserver = new ResizeObserver(() => computeOffset())

    computeOffset()

    const mutationObserver = new MutationObserver(() => {
      const current = document.querySelector(".announcementBanner") as HTMLElement | null
      if (current !== bannerEl) {
        if (bannerEl && resizeObserver) {
          resizeObserver.unobserve(bannerEl)
        }
        bannerEl = current
        computeOffset(current)
      }
    })

    mutationObserver.observe(document.body, { childList: true, subtree: true })

    const handleMediaChange = () => computeOffset()
    mediaQuery.addEventListener("change", handleMediaChange)

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange)
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
    }
  }, [isOpen])

  const mobileMenuTop = MOBILE_MENU_BASE_TOP + announcementOffset
  const menuPositionStyle: CSSProperties = useMemo(
    () => ({
      top: `${mobileMenuTop}px`,
      height: `calc(100dvh - ${mobileMenuTop}px)`,
    }),
    [mobileMenuTop]
  )
  const { showSignInModal } = useSigninModal()
  /**
   * 处理退出登录
   * 参考 PC 端 UserProfile 组件的退出登录逻辑
   */
  const handleSignOut = () => {
    deleteCookie(process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "", {
      domain: ".metal.com",
      path: "/",
    })
    window.location.reload()
  }

  if (!isOpen) return null
  return (
    <>
      {typeof window !== "undefined" && (
        <div className="mobileMenuOverlay" style={menuPositionStyle} onClick={closeMobileMenu} />
      )}
      <div
        ref={scrollContainerRef}
        className="mobileMenuPanel"
        style={menuPositionStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mobileNavList">
          {memoMenu.map((menuItem: MenuItem) => {
            const isExpanded = expandedMenuKey === menuItem.title
            const shouldShowItem = !selectedThirdLevelMenu || selectedThirdLevelMenu.parentTitle === menuItem.title
            if (menuItem.title === "Metrix") {
              if (selectedThirdLevelMenu) {
                return null
              }
              return <MetalMindLink key={menuItem.title} />
            }

            if (!shouldShowItem) {
              return null
            }

            return (
              <div key={menuItem.title} className="mobileNavItem">
                {/* 一级菜单头部：当三级菜单显示时隐藏 */}
                {(!selectedThirdLevelMenu || selectedThirdLevelMenu.parentTitle !== menuItem.title) && (
                  <div className="mobileNavItemHeader" onClick={() => toggleMenuExpansion(menuItem.title)}>
                    <span>{menuItem.title}</span>
                    <Iconfont
                      type="icon-xiayijijiantou"
                      style={{
                        color: "#0c1019",
                        fontSize: 16,
                        transition: "transform 0.3s ease",
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      }}
                    />
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {menuItem.title === marketsTitle && isExpanded && !selectedThirdLevelMenu && (
                    <motion.div
                      className="mobileNavSubMenu"
                      key={expandedMenuKey}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                        opacity: { duration: 0.2, ease: "easeOut" },
                      }}
                      style={{ overflow: "hidden" }}
                    >
                      <Link
                        href={getSiteUrl("www") + `/${locale}/watchlist`}
                        className="mobileWatchlistLink"
                        onClick={closeMobileMenu}
                      >
                        <span>{t("utilityNav.MyWatchlist")}</span>
                        <Iconfont
                          type="icon-a-Arrow-rightjiantouyou"
                          style={{ color: "#0C1019", fontSize: 14, marginLeft: 4 }}
                        />
                      </Link>
                      <div className="mobileNavSubMenuContent">
                        {menuItem.children?.map((item: NavigationItem) => (
                          <div
                            key={item.title?.toString()}
                            className="mobileNavSubMenuItem"
                            onClick={() => handleSecondLevelClick(menuItem.title, item)}
                            style={{ cursor: item.children && item.children.length > 0 ? "pointer" : "default" }}
                          >
                            {item.children && item.children.length > 0 ? (
                              <div className="mobileNavSubMenuLink">
                                {item.title}
                                {item.isHot && <Iconfont type="icon-huo" style={{ color: "#D7000F", fontSize: 12 }} />}
                                <Iconfont
                                  type="icon-xiayijijiantou"
                                  style={{ color: "#0C1019", fontSize: 16, marginLeft: "auto" }}
                                />
                              </div>
                            ) : (
                              <Link href={item.href || "#"} className="mobileNavSubMenuLink" onClick={closeMobileMenu}>
                                {item.title}
                                {item.isHot && <Iconfont type="icon-huo" style={{ color: "#D7000F", fontSize: 12 }} />}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {menuItem.title === marketsTitle &&
                  isExpanded &&
                  selectedThirdLevelMenu &&
                  selectedThirdLevelMenu.parentTitle === menuItem.title && (
                    <div className="mobileThirdLevelMenu">
                      <div className="mobileThirdLevelHeader">
                        <button className="mobileThirdLevelBackButton" onClick={handleBackToSecondLevel}>
                          <Iconfont
                            type="icon-xiayijijiantou"
                            style={{
                              color: "#0c1019",
                              fontSize: 16,
                              transform: "rotate(180deg)",
                            }}
                          />
                          <span>{t("common.Back")}</span>
                        </button>
                        <h2 className="mobileThirdLevelTitle">{selectedThirdLevelMenu.item.title}</h2>
                      </div>

                      <div className="mobileThirdLevelList">
                        {selectedThirdLevelMenu.item.children?.map((child, childIndex) => {
                          const key =
                            child.href && child.href !== "#"
                              ? child.href
                              : typeof child.title === "string"
                                ? `${child.title}-${childIndex}`
                                : `child-${childIndex}`

                          const href =
                            child.href && child.href !== "#" ? getSiteUrl("www") + "/" + locale + "/" + child.href : "#"

                          return (
                            <Link key={key} href={href} className={`mobileThirdLevelItem`} onClick={closeMobileMenu}>
                              {child.title}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  )}

                <AnimatePresence>
                  {menuItem.title !== marketsTitle && isExpanded && (
                    <motion.div
                      className="mobileNavSubMenuSecondary"
                      key={expandedMenuKey}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                        opacity: { duration: 0.2, ease: "easeOut" },
                      }}
                      style={{ overflow: "hidden" }}
                    >
                      {menuItem.children?.map((item: NavigationItem, itemIndex: number) => {
                        const key =
                          item.href && item.href !== "#"
                            ? item.href
                            : typeof item.title === "string"
                              ? `${item.title}-${itemIndex}`
                              : `secondary-${itemIndex}`

                        const href = item.href && item.href !== "#" ? item.href : "#"

                        return (
                          <Link
                            key={key}
                            href={href}
                            className="mobileNavSubMenuLinkSecondary"
                            onClick={closeMobileMenu}
                          >
                            {item.title}
                            <Iconfont
                              type="icon-xiayijijiantou"
                              style={{ color: "#0C1019", fontSize: 16, marginLeft: 4 }}
                            />
                          </Link>
                        )
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
        {!selectedThirdLevelMenu && (
          <div className="mobileMenuFooter">
            {!userInfo ? (
              <>
                <Button
                  className="mobileSignInButton"
                  onClick={() => {
                    closeMobileMenu()
                    showSignInModal({
                      language: locale,
                      action: "login",
                      animate: "drawer",
                      onLoginSuccess: () => {
                        closeMobileMenu()
                        window.location.reload()
                      },
                      onRegisterSuccess: () => {
                        closeMobileMenu()
                        window.location.reload()
                      },
                    })
                  }}
                >
                  {t("common.SignIn")}
                </Button>

                <div className="mobileActionButtons">
                  <Link href={getSiteUrl("www") + "/" + locale + "/app"} className="mobileActionButton">
                    {t("common.DownloadApp")}
                  </Link>
                  <Link href={getSiteUrl("user") + "/" + locale + "/subscribe"} className="mobileActionButton">
                    {t("common.Subscribe")}
                  </Link>
                </div>
              </>
            ) : (
              <div className="mobileSignOutContainer">
                <Row>
                  <button className="mobileSignOutButton" onClick={handleSignOut}>
                    {t("common.SignOut")}
                  </button>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Link href={getSiteUrl("www") + "/" + locale + "/app"} className="mobileLink">
                      {t("common.DownloadApp")}
                    </Link>
                  </Col>
                  <Col span={12}>
                    <Link href={getSiteUrl("user") + "/" + locale + "/subscribe"} className="mobileLink">
                      {t("common.Subscribe")}
                    </Link>
                  </Col>
                </Row>
              </div>
            )}
            <div className="mobileFooterLinks">
              <Link
                href={getSiteUrl("www") + "/" + locale + "/announcements"}
                className="mobileFooterLink"
                onClick={closeMobileMenu}
              >
                {t("utilityNav.Announcements")}
              </Link>
              <Link
                href={getSiteUrl("www") + "/" + locale + "/about-us"}
                className="mobileFooterLink"
                onClick={closeMobileMenu}
              >
                {t("utilityNav.AboutUs")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
